import React from 'react';

export default function ProposalTemplate() {
  return (
    <div className="proposal-document font-sans antialiased" style={{fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif"}}>
      <div className="header bg-blue-900 text-white py-12 px-8">
        <h1 className="text-3xl font-semibold mb-2">CENTENNI x NEXT GENERATION MEDICINE</h1>
        <p className="text-xl text-blue-100">Proposal for Strategic Clinical and Commercial Partnership</p>
      </div>
      
      <div className="content p-8">
        <div className="section mb-8">
          <h2 className="text-2xl font-semibold text-blue-900 mb-4">Overview</h2>
          <p className="text-gray-700 mb-4">
            Centenni is launching with a clear vision: to build a parallel health system grounded in longevity and performance. The founding team brings energy, insight, and a bold willingness to challenge convention. You're starting with the right questions—and now it's time to accelerate with precision, structure, and guidance.
          </p>
          <p className="text-gray-700 mb-4">
            This proposal outlines how we can partner together to help Centenni build a best-in-class clinical and commercial foundation. My role will be to embed with your team in a targeted, high-leverage capacity—supporting early-stage execution while ensuring every foundational decision points toward long-term scale.
          </p>
        </div>

        <div className="section mb-8">
          <h2 className="text-2xl font-semibold text-blue-900 mb-4">What I Bring</h2>
          <p className="text-gray-700 mb-4">
            Over the last decade, I've built, advised, and scaled some of the leading businesses in longevity medicine—including as Chief Medical Officer at Modern Age, advisor to Superpower, and Head of Longevity Medicine at Midi Health. My unique background blends:
          </p>
          <ul className="list-disc pl-6 mb-4 text-gray-700">
            <li className="mb-2">Harvard MD/MBA training</li>
            <li className="mb-2">Frontline clinical experience</li>
            <li className="mb-2">Startup operator leadership</li>
            <li className="mb-2">Deep immersion in the longevity space—from therapeutics to coaching to tech infrastructure</li>
          </ul>
          <p className="text-gray-700 mb-4">
            What sets my work apart is the ability to synthesize across domains—medicine, strategy, operations—and rapidly make ideas executable.
          </p>
        </div>

        <div className="section mb-8">
          <h2 className="text-2xl font-semibold text-blue-900 mb-4">Core Focus Areas for Centenni</h2>
          <p className="text-gray-700 mb-4">
            This partnership would focus on three critical domains:
          </p>
          
          <div className="bg-blue-50 border-l-4 border-blue-900 p-6 mb-6">
            <h3 className="text-xl font-medium text-blue-900 mb-3">Strategic Foundation</h3>
            <ul className="list-disc pl-6 text-gray-700">
              <li className="mb-2">Clinical program design tailored to your "Strava crowd" performance audience</li>
              <li className="mb-2">Offering architecture: what tests, therapies, and coaching to include and how to package them</li>
              <li className="mb-2">Business model strategy: pricing, membership model, monetization paths</li>
            </ul>
          </div>
          
          <div className="bg-blue-50 border-l-4 border-blue-900 p-6 mb-6">
            <h3 className="text-xl font-medium text-blue-900 mb-3">Clinical Infrastructure</h3>
            <ul className="list-disc pl-6 text-gray-700">
              <li className="mb-2">Protocol development support: drafting or reviewing early protocols to fit your philosophy and customer</li>
              <li className="mb-2">Talent calibration: vetting your first clinician, ensuring alignment with startup culture and performance medicine</li>
              <li className="mb-2">Knowledge base access: training materials to bring your care team up to speed, fast</li>
            </ul>
          </div>
          
          <div className="bg-blue-50 border-l-4 border-blue-900 p-6 mb-6">
            <h3 className="text-xl font-medium text-blue-900 mb-3">Executional Tools</h3>
            <ul className="list-disc pl-6 text-gray-700">
              <li className="mb-2">AI-powered diagnostic report generator to save time and elevate patient experience</li>
              <li className="mb-2">Clinical dashboards and decision frameworks</li>
              <li className="mb-2">Slack-based async support to guide real-time operational questions</li>
            </ul>
          </div>
        </div>

        <div className="section mb-8">
          <h2 className="text-2xl font-semibold text-blue-900 mb-4">Engagement Structure</h2>
          <p className="text-gray-700 mb-4">
            Given your timeline and current phase, I propose a two-phase model:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white shadow-md border-t-4 border-blue-900 p-6">
              <h3 className="text-xl font-medium text-blue-900 mb-2">Phase 1: Foundational Strategy<br/><span className="text-sm text-gray-600">(Pre-team Build)</span></h3>
              <p className="text-blue-900 font-medium mb-2">Recommended: Start Immediately</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li className="mb-1">Asynchronous advisory engagement</li>
                <li className="mb-1">Slack-based communication + document feedback + asset sharing</li>
                <li className="mb-1">Occasional check-ins as needed</li>
              </ul>
              <p className="text-xl font-semibold text-blue-900">Fee: $1,000/month</p>
              <p className="text-gray-700 mt-4 text-sm">
                Starting now allows us to build the scaffolding ahead of hiring—so your incoming team can operate from day one with clarity, speed, and focus.
              </p>
            </div>
            
            <div className="bg-white shadow-md border-t-4 border-blue-900 p-6">
              <h3 className="text-xl font-medium text-blue-900 mb-2">Phase 2: Clinical and Product Execution<br/><span className="text-sm text-gray-600">(Post-team Build)</span></h3>
              <p className="text-blue-900 font-medium mb-2">Recommended: Start ~July</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li className="mb-1">Deeper involvement in protocol refinement, clinician enablement, and product execution</li>
                <li className="mb-1">Support across MVP care model, outcome delivery, and brand translation into clinical operations</li>
              </ul>
              <p className="text-xl font-semibold text-blue-900">Fee: To be determined based on scope and needs</p>
            </div>
          </div>
        </div>

        <div className="section mb-10">
          <h2 className="text-2xl font-semibold text-blue-900 mb-4">Why This Works</h2>
          <ul className="list-disc pl-6 text-gray-700 mb-6">
            <li className="mb-2">Most early-stage companies waste time trying to figure out the wrong problems. I help ensure you're solving the right ones—faster, and with a framework that scales.</li>
            <li className="mb-2">You don't need five advisors. You need one who understands the whole system and knows how to build in complexity without adding chaos.</li>
            <li className="mb-2">My goal is not just to help you move quickly—but to make sure what you build actually works.</li>
          </ul>
        </div>

        <div className="section mb-8 border-t border-gray-200 pt-8">
          <h2 className="text-2xl font-semibold text-blue-900 mb-4">Next Steps</h2>
          <p className="text-gray-700 mb-4">
            Let me know when you're ready to engage. I'll also share a teaser of materials so you can get a sense of what working together looks like.
          </p>
          <p className="text-gray-700 font-medium">
            Looking forward to building with you.
          </p>
        </div>
        
        <div className="footer border-t border-gray-200 pt-6 text-gray-700">
          <p className="font-semibold">Anant Vinjamoori, MD MBA</p>
          <p>Founder & President</p>
          <p>Next Generation Medicine</p>
          <p className="text-blue-900">www.nextgenerationmedicine.co</p>
        </div>
      </div>
    </div>
  );
}